import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, X, Download, CheckCircle2 } from "lucide-react";

interface Charter {
  projectName: string;
  sponsor: string;
  projectManager: string;
  ba: string;
  methodology: "Agile" | "Waterfall" | "Hybrid";
  targetUser: string;
  userProblem: string;
  successMetric: string;
  rootCause: string;
  objectives: string[];
  inScope: string[];
  outOfScope: string[];
  constraints: string[];
  assumptions: string[];
  budget: string;
  milestones: { name: string; date: string }[];
  approved: boolean;
  approvedBy: string;
  approvedDate: string;
}

const EMPTY: Charter = {
  projectName: "", sponsor: "", projectManager: "", ba: "",
  methodology: "Agile",
  targetUser: "", userProblem: "", successMetric: "", rootCause: "",
  objectives: [], inScope: [], outOfScope: [],
  constraints: [], assumptions: [],
  budget: "", milestones: [],
  approved: false, approvedBy: "", approvedDate: "",
};

function ListEditor({ label, items, onChange }: { label: string; items: string[]; onChange: (v: string[]) => void }) {
  const [draft, setDraft] = useState("");
  const add = () => { if (draft.trim()) { onChange([...items, draft.trim()]); setDraft(""); } };
  return (
    <div>
      <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">{label}</label>
      <div className="space-y-1 mb-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-[12px] bg-muted/40 rounded px-2 py-1">
            <span className="flex-1">{item}</span>
            <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive">
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === "Enter" && add()}
          placeholder={`Add ${label.toLowerCase()}…`} className="h-7 text-xs flex-1" />
        <Button size="sm" variant="outline" className="h-7 px-2" onClick={add}><Plus className="h-3 w-3" /></Button>
      </div>
    </div>
  );
}

export default function ProjectCharterPanel() {
  const [charter, setCharter] = useState<Charter>(EMPTY);
  const set = (k: keyof Charter, v: any) => setCharter(c => ({ ...c, [k]: v }));

  const exportTxt = () => {
    const lines = [
      `PROJECT CHARTER`,
      `═══════════════════════════════════════`,
      `Project: ${charter.projectName}`,
      `Sponsor: ${charter.sponsor}`,
      `PM: ${charter.projectManager}  |  BA: ${charter.ba}`,
      `Methodology: ${charter.methodology}`,
      `Budget: ${charter.budget}`,
      ``,
      `PROBLEM DEFINITION`,
      `Target User:    ${charter.targetUser}`,
      `User Problem:   ${charter.userProblem}`,
      `Success Metric: ${charter.successMetric}`,
      `Root Cause:     ${charter.rootCause}`,
      ``,
      `OBJECTIVES`,
      ...charter.objectives.map(o => `  • ${o}`),
      ``,
      `IN SCOPE`,
      ...charter.inScope.map(s => `  ✓ ${s}`),
      ``,
      `OUT OF SCOPE`,
      ...charter.outOfScope.map(s => `  ✗ ${s}`),
      ``,
      `CONSTRAINTS`,
      ...charter.constraints.map(c => `  ⚠ ${c}`),
      ``,
      `ASSUMPTIONS`,
      ...charter.assumptions.map(a => `  ~ ${a}`),
      ``,
      `MILESTONES`,
      ...charter.milestones.map(m => `  ${m.date}  ${m.name}`),
      ``,
      charter.approved ? `APPROVED by ${charter.approvedBy} on ${charter.approvedDate}` : `STATUS: Pending approval`,
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "project-charter.txt"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 border-b border-border px-5 py-3 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold">Project Charter</h2>
          <p className="text-[11px] text-muted-foreground">Define scope, objectives, and authority before work begins.</p>
        </div>
        <div className="flex gap-2">
          {charter.approved && <Badge variant="outline" className="text-[10px] bg-green-100 text-green-700 border-green-300 gap-1"><CheckCircle2 className="h-3 w-3" /> Approved</Badge>}
          <Button size="sm" variant="outline" className="h-7 gap-1 text-[11px]" onClick={exportTxt}><Download className="h-3 w-3" /> Export</Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6" style={{ scrollbarWidth: "thin" }}>

        {/* Header fields */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground block mb-1">Project Name</label>
            <Input value={charter.projectName} onChange={e => set("projectName", e.target.value)} className="h-8 text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground block mb-1">Sponsor</label>
            <Input value={charter.sponsor} onChange={e => set("sponsor", e.target.value)} className="h-8 text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground block mb-1">Project Manager</label>
            <Input value={charter.projectManager} onChange={e => set("projectManager", e.target.value)} className="h-8 text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground block mb-1">Business Analyst</label>
            <Input value={charter.ba} onChange={e => set("ba", e.target.value)} className="h-8 text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground block mb-1">Methodology</label>
            <select value={charter.methodology} onChange={e => set("methodology", e.target.value)}
              className="w-full h-8 text-sm border border-input rounded-md px-2 bg-background text-foreground">
              <option>Agile</option><option>Waterfall</option><option>Hybrid</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground block mb-1">High-Level Budget</label>
            <Input value={charter.budget} onChange={e => set("budget", e.target.value)} placeholder="e.g. $480,000" className="h-8 text-sm" />
          </div>
        </div>

        {/* Structured problem definition */}
        <div className="space-y-3">
          <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Problem Definition</p>
          <div className="border border-border rounded-lg p-4 space-y-3 bg-muted/10">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground block mb-1">
                Target User
              </label>
              <Textarea value={charter.targetUser} onChange={e => set("targetUser", e.target.value)}
                placeholder="Who are the primary users? (e.g. 'Small-team project managers in local government who lack dedicated tooling')"
                rows={2} className="text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground block mb-1">
                User Problem
              </label>
              <Textarea value={charter.userProblem} onChange={e => set("userProblem", e.target.value)}
                placeholder="What problem do they face, and what are the consequences? (e.g. 'Teams lose institutional knowledge when staff turn over — decisions, rationale, and context exist nowhere but people's heads')"
                rows={3} className="text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground block mb-1">
                Success Metric
              </label>
              <Textarea value={charter.successMetric} onChange={e => set("successMetric", e.target.value)}
                placeholder="How will we know we've solved it? What measurably changes? (e.g. 'New team members onboard in under 2 weeks vs current 6 weeks; zero undocumented decisions at handover')"
                rows={2} className="text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground block mb-1">
                Root Cause
              </label>
              <Textarea value={charter.rootCause} onChange={e => set("rootCause", e.target.value)}
                placeholder="Why does this problem exist today? What has prevented it being solved? (e.g. 'No structured format for capturing decisions — teams default to email threads and tribal knowledge')"
                rows={2} className="text-sm" />
            </div>
          </div>
        </div>

        {/* Lists */}
        <ListEditor label="Objectives" items={charter.objectives} onChange={v => set("objectives", v)} />
        <div className="grid grid-cols-2 gap-4">
          <ListEditor label="In Scope" items={charter.inScope} onChange={v => set("inScope", v)} />
          <ListEditor label="Out of Scope" items={charter.outOfScope} onChange={v => set("outOfScope", v)} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <ListEditor label="Constraints" items={charter.constraints} onChange={v => set("constraints", v)} />
          <ListEditor label="Assumptions" items={charter.assumptions} onChange={v => set("assumptions", v)} />
        </div>

        {/* Milestones */}
        <div>
          <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">Key Milestones</label>
          <div className="space-y-1.5 mb-2">
            {charter.milestones.map((m, i) => (
              <div key={i} className="flex items-center gap-2 bg-muted/40 rounded px-2 py-1">
                <span className="text-[11px] font-mono text-muted-foreground w-24">{m.date}</span>
                <span className="text-[12px] flex-1">{m.name}</span>
                <button onClick={() => set("milestones", charter.milestones.filter((_, j) => j !== i))}
                  className="text-muted-foreground hover:text-destructive"><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
          <MilestoneAdder onAdd={m => set("milestones", [...charter.milestones, m])} />
        </div>

        {/* Approval */}
        <div className="border border-border rounded-lg p-4 space-y-3">
          <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Approval</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-muted-foreground block mb-1">Approved by</label>
              <Input value={charter.approvedBy} onChange={e => set("approvedBy", e.target.value)} className="h-7 text-xs" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground block mb-1">Date</label>
              <Input type="date" value={charter.approvedDate} onChange={e => set("approvedDate", e.target.value)} className="h-7 text-xs" />
            </div>
          </div>
          <Button size="sm" variant={charter.approved ? "outline" : "default"} className="gap-1.5 text-xs"
            onClick={() => set("approved", !charter.approved)}>
            <CheckCircle2 className="h-3.5 w-3.5" />
            {charter.approved ? "Revoke approval" : "Mark as approved"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function MilestoneAdder({ onAdd }: { onAdd: (m: { name: string; date: string }) => void }) {
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const add = () => { if (name.trim() && date) { onAdd({ name: name.trim(), date }); setName(""); setDate(""); } };
  return (
    <div className="flex gap-2">
      <Input value={date} type="date" onChange={e => setDate(e.target.value)} className="h-7 text-xs w-36" />
      <Input value={name} onChange={e => setName(e.target.value)} placeholder="Milestone name" className="h-7 text-xs flex-1" onKeyDown={e => e.key === "Enter" && add()} />
      <Button size="sm" variant="outline" className="h-7 px-2" onClick={add}><Plus className="h-3 w-3" /></Button>
    </div>
  );
}
